import { useCallback, useEffect, useRef, useState } from 'react';
import { AmlCallView } from '../../phone-calls/types';
import { FeatureLayer, Graphic, WebMap } from '../constructors';
import { getAmlCallLastAmlLocationGeoPointEntity } from '../helpers/aml';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import { createAmlFeatureLayer } from '../helpers/feature-layers';
import { createAmlGraphic } from '../helpers/graphics';
import type { AmlLocationGeoPointEntity } from '../types';
import { useLayerControl, usePreviousLayer } from './use-feature-layer-control';
import { areLatLngsEqual } from '../../location/helpers';
import { EsriMapEntityTypeEnum } from '../types';
import { useFeatureLayerVisibility } from './use-feature-layer-visibility';
import { useFeatureLayerCleanup } from './use-feature-layer-cleanup';

// show current AML (RapidSOS) icon on map

type AmlFeatureLayerProps = {
    amlCall?: AmlCallView;
    selectedAmlPhoneNumberId?: number;
    webMap?: WebMap;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
    areRapidSOSAniAliLayersVisible: boolean;
};
export const useAmlFeatureLayer = ({
    amlCall,
    webMap,
    selectedAmlPhoneNumberId,
    selectedMapLedgerEntityType,
    areRapidSOSAniAliLayersVisible,
}: AmlFeatureLayerProps) => {
    const amlLocationGeoPointEntity = getAmlCallLastAmlLocationGeoPointEntity(amlCall);

    // There will be only one AML location in amlFeatureLayer.
    const aml: AmlLocationGeoPointEntity[] = amlLocationGeoPointEntity
        ? [amlLocationGeoPointEntity]
        : [];
    const [amlLayer, setAmlLayer] = useState<FeatureLayer>();
    const prevSelectedAmlCallId = usePreviousLayer(selectedAmlPhoneNumberId, amlLayer);
    const prevSelectedMapLedgerEntityType = usePreviousLayer(selectedMapLedgerEntityType, amlLayer);

    const entityIdToObjectIdMapCacheRef = useRef<typeof EntityIdToObjectIdMaps.AML>(
        EntityIdToObjectIdMaps.AML
    );

    const didAmlCallGraphicChange = useCallback(
        (currCall: AmlLocationGeoPointEntity, prevCall: AmlLocationGeoPointEntity) => {
            const isEntityTypeChanged =
                prevSelectedMapLedgerEntityType !== selectedMapLedgerEntityType &&
                (prevSelectedMapLedgerEntityType === EsriMapEntityTypeEnum.AML_CALL ||
                    selectedMapLedgerEntityType === EsriMapEntityTypeEnum.AML_CALL);

            // Force change detection if entity Id changed (new AML location)
            const entityIdChanged = currCall.entityId !== prevCall.entityId;

            return (
                isEntityTypeChanged ||
                entityIdChanged ||
                !areLatLngsEqual(currCall, prevCall) ||
                selectedAmlPhoneNumberId !== prevSelectedAmlCallId
            );
        },
        [
            prevSelectedAmlCallId,
            selectedAmlPhoneNumberId,
            prevSelectedMapLedgerEntityType,
            selectedMapLedgerEntityType,
        ]
    );

    const createAmlGraphics = useCallback(
        (amlLocationGeoPointEntities: AmlLocationGeoPointEntity[]) => {
            const amlGraphics: Graphic[] = [];
            const amlLocationGeoPointEntity =
                amlLocationGeoPointEntities[amlLocationGeoPointEntities.length - 1];

            if (amlLocationGeoPointEntity) {
                const objectId = entityIdToObjectIdMapCacheRef.current.get(
                    amlLocationGeoPointEntity.entityId
                );
                const amlGraphic = createAmlGraphic({
                    objectId,
                    amlLocationGeoPointEntity,
                    selectedAmlPhoneNumberId,
                });
                if (amlGraphic) {
                    amlGraphics.push(amlGraphic);
                }
            }

            return amlGraphics;
        },
        [selectedAmlPhoneNumberId]
    );

    // handles initializing empty feature layer
    useEffect(() => {
        if (webMap) {
            const amlFeatureLayer = createAmlFeatureLayer();
            setAmlLayer(amlFeatureLayer);
            webMap.layers.add(amlFeatureLayer);
        }
    }, [webMap]);

    useFeatureLayerVisibility({
        featureLayer: amlLayer,
        isVisible: areRapidSOSAniAliLayersVisible,
    });

    useFeatureLayerCleanup({
        layer: amlLayer,
        webMap,
        cacheKey: 'AML',
        entityIdToObjectIdMapCacheRef,
        currentItems: aml,
        cleanupStrategy: 'reset-all',
        getCurrentEntityIds: (items) => items.map((item) => item.entityId),
    });

    useLayerControl<AmlLocationGeoPointEntity>({
        layer: amlLayer,
        currItems: aml,
        cacheKey: 'AML',
        enforceSingleFeatureInvariant: true,
        createGraphics: createAmlGraphics,
        didItemChange: didAmlCallGraphicChange,
        entityIdToObjectIdMapCacheRef,
    });

    return { amlLayer };
};
